/**
 * The existing-deck picker of step 3 (handoff §6.1, «Существующая колода»).
 *
 * A bottom sheet on a phone and a centred dialog on desktop (the width the save sheet
 * switches at), listing the learner's own decks with a search field. Only an own, non-system deck can be
 * chosen (ADR-115 Decision 2). The deck list is the app's: `useDecks`.
 */
import { useEffect, useState } from 'react'
import { Check, Plus, Search, X } from 'lucide-react'
import { useLocale } from '../../i18n/context'
import { isDeckOwner, useDecks } from '../../hooks/useDecks'
import { useSwipeNavBlock } from '../../hooks/useSwipeNavBlock'
import { useIsDesktop } from '../../hooks/useIsDesktop'
import { useAuthStore, selectUser } from '../../stores/authStore'
import { CONTROL_SURFACE, suppressTouchContextMenu } from '../../utils/controlSurface'

interface Props {
  open: boolean
  onClose: () => void
  onPick: (deck: { id: string; title: string }) => void
  /**
   * Edit mode's «Привязать колоду»: the decks already linked are marked, a tap on any
   * row toggles it (the page decides link or unlink), and the footnote says linking
   * makes no cards. Without it this is step 3's single-choice picker.
   */
  linking?: { linkedIds: string[]; title: string; linked: string; link: string; footnote: string }
  /** A first row that makes a new deck instead (edit mode's «Новая колода», ADR-115 Amendment 4). */
  newDeck?: { label: string; onPick: () => void }
}

export default function DeckPickerSheet({ open, onClose, onPick, linking, newDeck }: Props) {
  const { tr } = useLocale()
  const t = tr.textFlow.save
  const user = useAuthStore(selectUser)
  const desktop = useIsDesktop()
  const [search, setSearch] = useState('')
  const decks = useDecks(1, 100, null, search || undefined)
  useSwipeNavBlock(open, 'text-flow-deck-picker')

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null
  const own = (decks.data?.items ?? []).filter((d) => isDeckOwner(d, user?.id))

  return (
    <div
      className={CONTROL_SURFACE}
      onContextMenu={suppressTouchContextMenu}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, display: 'flex', justifyContent: 'center',
        alignItems: desktop ? 'center' : 'flex-end', padding: desktop ? 16 : 0,
      }}
    >
      <div onClick={onClose} className="add-sheet-scrim" style={{ position: 'absolute', inset: 0 }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={linking?.title ?? t.pickerTitle}
        style={{
          position: 'relative', width: '100%', maxWidth: desktop ? 440 : 480, maxHeight: '80dvh', display: 'flex',
          flexDirection: 'column', background: 'var(--bg-surface)', borderRadius: desktop ? 16 : '16px 16px 0 0',
          boxShadow: 'var(--shadow-float)', fontFamily: 'var(--font-ui)',
          ...(desktop ? { border: '1px solid var(--border)', paddingBottom: 12 } : { paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }),
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 8px 0 20px' }}>
          <h2 style={{ flex: 1, margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--text-primary)' }}>{linking?.title ?? t.pickerTitle}</h2>
          <button
            type="button" onClick={onClose} aria-label={tr.textFlow.close}
            style={{ width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)' }}
          >
            <X size={20} />
          </button>
        </div>
        <div style={{ position: 'relative', margin: '4px 16px 8px' }}>
          <Search size={16} aria-hidden style={{ position: 'absolute', left: 14, top: 16, color: 'var(--text-tertiary)' }} />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t.pickerSearch}
            aria-label={t.pickerSearch}
            style={{
              width: '100%', height: 48, boxSizing: 'border-box', padding: '0 16px 0 38px', borderRadius: 8,
              border: '1px solid var(--border)', background: 'var(--bg-secondary)', color: 'var(--text-primary)',
              fontSize: 15, fontFamily: 'var(--font-ui)',
            }}
          />
        </div>
        <ul style={{ listStyle: 'none', margin: 0, padding: '0 8px', overflowY: 'auto' }}>
          {newDeck && (
            <li>
              <button
                type="button"
                data-testid="picker-new-deck"
                onClick={newDeck.onPick}
                style={{
                  width: '100%', minHeight: 56, padding: '0 12px', textAlign: 'left', borderRadius: 8,
                  display: 'flex', alignItems: 'center', gap: 8, background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 15, fontWeight: 600, color: 'var(--accent)', fontFamily: 'var(--font-ui)',
                }}
              >
                <Plus size={18} aria-hidden />
                {newDeck.label}
              </button>
            </li>
          )}
          {decks.isLoading && <li style={{ padding: 16, fontSize: 14, color: 'var(--text-secondary)' }}>{t.pickerLoading}</li>}
          {!decks.isLoading && own.length === 0 && (
            <li style={{ padding: 16, fontSize: 14, color: 'var(--text-secondary)' }}>{t.pickerEmpty}</li>
          )}
          {own.map((d) => (
            <li key={d.id}>
              <button
                type="button"
                onClick={() => onPick({ id: d.id, title: d.title })}
                style={{
                  width: '100%', minHeight: linking ? 56 : 48, padding: '0 12px', textAlign: 'left', borderRadius: 8,
                  display: 'flex', alignItems: 'center', gap: 8,
                  background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 500,
                  color: 'var(--text-primary)', fontFamily: 'var(--font-ui)',
                }}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</span>
                {linking && (
                  <span
                    data-testid={linking.linkedIds.includes(d.id) ? 'picker-linked' : 'picker-link'}
                    style={{
                      flexShrink: 0, height: 32, padding: '0 12px', display: 'inline-flex', alignItems: 'center', gap: 4,
                      borderRadius: 'var(--radius-pill)', fontSize: 13, fontWeight: 600,
                      ...(linking.linkedIds.includes(d.id)
                        ? { background: 'var(--accent-soft)', color: 'var(--accent)' }
                        : { border: '1px solid var(--border)', color: 'var(--accent)' }),
                    }}
                  >
                    {linking.linkedIds.includes(d.id) ? <><Check size={14} aria-hidden />{linking.linked}</> : linking.link}
                  </span>
                )}
              </button>
            </li>
          ))}
        </ul>
        {linking && (
          <p style={{ margin: 0, padding: '8px 20px 4px', fontSize: 12, lineHeight: 1.45, color: 'var(--text-tertiary)' }}>
            {linking.footnote}
          </p>
        )}
      </div>
    </div>
  )
}
